import React, { useState, useEffect } from 'react';
import { apiClient } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, CheckCircle, AlertCircle, Plus, X } from 'lucide-react';

export function OrganizationSettings() {
  const { organization, refreshUser } = useAuth();
  const [orgName, setOrgName] = useState(organization?.name || '');
  const [departments, setDepartments] = useState<string[]>(
    organization?.settings?.departments_enabled || []
  );
  const [newDepartment, setNewDepartment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Reset form when organization changes
  useEffect(() => {
    setOrgName(organization?.name || '');
    setDepartments(organization?.settings?.departments_enabled || []);
  }, [organization]);

  const handleAddDepartment = () => {
    if (newDepartment.trim() && !departments.includes(newDepartment.trim())) {
      setDepartments([...departments, newDepartment.trim()]);
      setNewDepartment('');
    }
  };

  const handleRemoveDepartment = (dept: string) => {
    setDepartments(departments.filter(d => d !== dept));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setIsLoading(true);

    try {
      await apiClient.updateOrganization({
        name: orgName,
        settings: {
          ...organization?.settings,
          departments_enabled: departments,
        },
      });

      setSuccess(true);
      await refreshUser(); // Refresh user data to get updated organization
      
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update organization');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="bg-white border rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-6">Organization Settings</h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Organization Name */}
          <div>
            <Label htmlFor="orgName">Organization Name *</Label>
            <Input
              id="orgName"
              type="text"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="Your Organization"
              required
              disabled={isLoading}
              className="mt-1"
            />
          </div>

          {/* Departments */}
          <div>
            <Label>Enabled Departments</Label>
            <p className="text-sm text-muted-foreground mb-3">
              Configure which departments are available for user assignment
            </p>

            {/* Department List */}
            <div className="space-y-2 mb-3">
              {departments.map((dept) => (
                <div
                  key={dept}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                >
                  <span className="text-sm font-medium">{dept}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveDepartment(dept)}
                    disabled={isLoading}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {departments.length === 0 && (
                <p className="text-sm text-muted-foreground italic py-4 text-center">
                  No departments configured
                </p>
              )}
            </div>

            {/* Add Department */}
            <div className="flex items-center space-x-2">
              <Input
                type="text"
                value={newDepartment}
                onChange={(e) => setNewDepartment(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddDepartment();
                  }
                }}
                placeholder="e.g., Radiology"
                disabled={isLoading}
              />
              <Button
                type="button"
                onClick={handleAddDepartment}
                disabled={isLoading || !newDepartment.trim()}
                variant="outline"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm flex items-start space-x-2">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Organization settings updated successfully!</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm flex items-start space-x-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end pt-4">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
